import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
// import { doc } from '../firebase/firestore'
import type { UserCompanyRole } from './userCompaniesInterface'
export interface User {
  id?: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  created_at: Timestamp;
  updated_at: Timestamp;
  active: boolean;
}

/**
 * Get all companies where the user's role is strictly 'user'.
 * @param userId The user's UID
 * @returns Array of company objects where the user has role 'user' only
 */
export async function hasUserRole(userId: string) {
    try {
        // Get all user-company relationships for this user
        const userCompaniesRef = collection(db, 'userCompanies');
        const q = query(
        userCompaniesRef,
            where('userId', '==', userId),
            where('role', 'in', ['user', 'superadmin']),
            where('active', '==', true)
        );
        const userCompaniesSnap = await getDocs(q);
        return !userCompaniesSnap.empty
    } catch (error) {
        console.error('Error in getUserOnlyCompanies:', error);
        return [];
    }
}

// get all the companies related to the userId
export async function getUserCompanies(userId: string) {
  try {
    // Get all user-company relationships for this user (all roles, not just 'user')
    const userCompaniesRef = collection(db, 'userCompanies');
    const q = query(
      userCompaniesRef,
      where('userId', '==', userId),
      where('active', '==', true)
    );
    const userCompaniesSnap = await getDocs(q);
    
    if (userCompaniesSnap.empty) {
      console.log('No active user-company relationships found for userId:', userId);
      return [];
    }

    // Extract user-company data
    const userCompaniesData: UserCompanyRole[] = userCompaniesSnap.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        userId: data.userId,
        companyId: data.companyId,
        role: data.role,
        permissions: data.permissions || [],
        active: data.active,
        created_at: data.created_at,
        updated_at: data.updated_at,
        created_by: data.created_by
      } as UserCompanyRole;
    });

    console.log('User companies data:', userCompaniesData);

    // Get unique company IDs
    const companyIds = [...new Set(userCompaniesData.map(uc => uc.companyId))];
    console.log('Company IDs to fetch:', companyIds);

    if (companyIds.length === 0) {
      return [];
    }

    // Get company details for these companyIds
    const companiesRef = collection(db, 'companies');
    const companiesQuery = query(companiesRef, where('__name__', 'in', companyIds));
    const companiesSnap = await getDocs(companiesQuery);
    
    // Create a map of companyId -> company data for easy lookup
    const companiesMap = new Map();
    companiesSnap.docs.forEach(doc => {
      companiesMap.set(doc.id, { id: doc.id, ...doc.data() });
    });

    console.log('Companies map:', companiesMap);

    // Combine user-company data with company details
    const result = userCompaniesData.map(userCompany => {
      const company = companiesMap.get(userCompany.companyId);
      
      return {
        // User-company relationship data
        id: userCompany.id,
        userId: userCompany.userId,
        companyId: userCompany.companyId,
        role: userCompany.role,
        permissions: userCompany.permissions || [],
        active: userCompany.active,
        created_at: userCompany.created_at,
        updated_at: userCompany.updated_at,
        created_by: userCompany.created_by,
        
        // Company details
        companyName: company?.name || 'Unknown Company',
        companyDescription: company?.description || null,
        companyActive: company?.active || false,
        companyCreatedAt: company?.created_at || null,
        companyLogo: company?.logo || null,
        companyConfig: company?.config || null
      };
    });

    console.log('Final result:', result);
    return result;

  } catch (error) {
    console.error('Error in getUserOnlyCompanies:', error);
    return [];
  }
}
